/**
 * Persists site details via upsert and writes the selected site type onto
 * medical_pa_order_status.npi_type.
 *
 * @module useSiteDetailsSave
 */
import { createLogger } from "@risalabs_frontend_org/logger";
import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { SiteNpiDetailsService } from "../../../../api/services/siteNpiDetails/siteNpiDetailsService";
import { SingleOrderDetailsTableName } from "../../../../enums/singleOrderDetailsTableName";
import { useUpdateOrderDetails } from "../../../../hooks/singleMedicalPaOrderDetails/useUpdateOrderDetails";
import { fetchAllTestLocationForOrg } from "../../../../redux/slice/testLocationDetailsSlice";
import { AppDispatch } from "../../../../redux/store/store";
import { ToastMessages } from "../../../toasts/toastMessages";
import {
  isInternalSiteType,
  isSiteDetailsFormValid,
  mapSiteDetailsFormToUpsertPayload,
  SiteDetailsFormValues,
} from "../siteDetailsForm.types";

const log = createLogger("AddSiteNpiModal", "useSiteDetailsSave.ts");

/** Parameters for {@link useSiteDetailsSave}. */
interface UseSiteDetailsSaveParams {
  /** Clears the opened modal id in Redux after a successful save. */
  onClose: () => void;
  /** Optional callback invoked with saved form values after upsert succeeds. */
  onSaveSuccess?: (values: SiteDetailsFormValues) => void;
  /** Optional callback after the org test location cache is force-refreshed. */
  onSubmissionUpdated?: () => Promise<void>;
  /** Reloads the Postgres `/order` document so `npi_type` is current. */
  fetchOrder?: (
    orderId: string,
    orgId: string,
    options?: { hideLoader?: boolean },
  ) => Promise<{ success: boolean }>;
  orderId: string;
  orgId: string;
}

/**
 * Handles Site NPI modal save: upserts org test location details for external
 * sites, writes the form type onto medical_pa_order_status.npi_type, reloads
 * the `/order` document, then closes the modal.
 *
 * An internal site has no location fields, so save skips the catalog upsert.
 * The npi_type write runs for both internal and external sites.
 *
 * @param params - Close handler and optional post-save callbacks.
 * @returns Save handler and in-flight loading flag for the modal footer.
 */
export const useSiteDetailsSave = ({
  onClose,
  onSaveSuccess,
  onSubmissionUpdated,
  fetchOrder,
  orderId,
  orgId,
}: UseSiteDetailsSaveParams) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isSaving, setIsSaving] = useState(false);
  const { runUpdate: updateNpiType } = useUpdateOrderDetails<{
    npi_type: string;
  }>({
    orderId,
    orgId,
    tableName: SingleOrderDetailsTableName.ORDER_STATUS,
  });

  const handleSave = useCallback(
    async (values: SiteDetailsFormValues) => {
      if (!isSiteDetailsFormValid(values)) {
        return;
      }

      const isInternal = isInternalSiteType(values.type);
      setIsSaving(true);

      try {
        if (!isInternal) {
          await SiteNpiDetailsService.upsertTestLocationDetails(
            mapSiteDetailsFormToUpsertPayload(values),
          );
          await dispatch(fetchAllTestLocationForOrg(true));
        }

        const statusUpdate = await updateNpiType({
          npi_type: values.type.trim(),
        });
        if (!statusUpdate.success) {
          throw statusUpdate.error ?? new Error("Failed to update npi_type");
        }

        if (!isInternal) {
          await onSubmissionUpdated?.();
        }
        const orderRefresh = await fetchOrder?.(orderId, orgId, {
          hideLoader: true,
        });
        if (orderRefresh && !orderRefresh.success) {
          log.error("Error refreshing order after site details save");
          toast.error(ToastMessages.SITE_DETAILS_ORDER_REFRESH_FAILURE);
        } else {
          toast.success(ToastMessages.SITE_DETAILS_SAVE_SUCCESS);
        }
        onSaveSuccess?.(values);
        onClose();
      } catch (error) {
        log.error("Error saving site details", error);
        toast.error(ToastMessages.SITE_DETAILS_SAVE_FAILURE);
      } finally {
        setIsSaving(false);
      }
    },
    [
      dispatch,
      fetchOrder,
      onClose,
      onSaveSuccess,
      onSubmissionUpdated,
      orderId,
      orgId,
      updateNpiType,
    ],
  );

  return { isSaving, handleSave };
};
